import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import Immutable, { OrderedMap, type List, OrderedSet } from 'immutable';
import { Node, Taxonomy } from '@uncinc/react-drupal-core';
import type { ImmutableEntity, TaxonomyEntity, ViewProps } from '@uncinc/react-drupal-core/types';

import { NodeFAQ } from '../../../types/uncinc_profile/nodes';

const useNodeFaqGrouping = (entities: List<ImmutableEntity<NodeFAQ>>):
[
  OrderedMap<number, OrderedSet<ImmutableEntity<NodeFAQ>>>,
  OrderedMap<number, TaxonomyEntity | null>,
] => (
  useMemo(() => {
    let nodes = OrderedMap<number, OrderedSet<ImmutableEntity<NodeFAQ>>>();
    let themes = OrderedMap<number, TaxonomyEntity | null>();

    entities.forEach((term) => {
      const themeId = term.getIn(['field_themes', 0, 'target_id'], 0) ?? 0;
      const group = nodes.get(themeId, OrderedSet<ImmutableEntity<NodeFAQ>>());

      const themeEntity = term.getIn(['field_themes', 0, 'entity']) || null;

      nodes = nodes.set(themeId, group.add(term));

      themes = themes.set(themeId, themeEntity);
    });

    return [nodes, themes];
  }, [entities])
);

interface FaqByThemeProps extends ViewProps {
  args: number[];
  entities: List<ImmutableEntity<NodeFAQ>>;
}

const FaqByTheme: React.FC<FaqByThemeProps> = ({ args: [parentThemeId], entities }) => {
  const { t } = useTranslation();
  const groupedEntities = useNodeFaqGrouping(entities);

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entities.map((node) => ({
      '@type': 'Question',
      name: node.getIn(['title', 0, 'value']),
      acceptedAnswer: {
        '@type': 'Answer',
        text: node.getIn(['body', 0, 'processed']),
      },
    })),
  });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {schema}
        </script>
      </Helmet>

      <div className={c('Faq')}>
        {groupedEntities[0].map((nodes, themeId) => {
          const theme = groupedEntities[1].get(themeId);
          const immutableTheme = Immutable.fromJS(theme);

          return (
            // eslint-disable-next-line react/no-array-index-key
            <div className="mb-16" key={`${themeId}:teaser`}>
              {(themeId && themeId !== parentThemeId) ? (
                <Taxonomy
                  id={themeId}
                  entity={immutableTheme}
                  viewMode="title"
                />
              ) : (
                <h2>
                  {t('pages.faq.general')}
                </h2>
              )}
              {nodes.map((node) => {
                const nodeId = node.getIn(['nid', 0, 'value']);
                const langcode = node.getIn(['langcode', 0, 'value']);

                return (
                  <Node
                    key={`node:${nodeId}:${langcode}`}
                    id={nodeId}
                    showLabel={false}
                    entity={node}
                  />
                );
              })}
            </div>
          );
        }).toList()}
      </div>
    </>
  );
};

export default FaqByTheme;
